import React, { useState, useEffect } from 'react';
import { X, Save, Type } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import { FIELD_TYPES } from '../../utils/constants';

const FieldFormModal = ({ isOpen, onClose, onSave, field, loading }) => {
  const [formData, setFormData] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text',
    field_options: '',
    placeholder: '',
    is_required: false,
    is_active: true,
    show_on_badge: false,
    is_global: false,
  });

  const [errors, setErrors] = useState({});
  
  // Reserved field names that cannot be used for custom fields
  const reservedFieldNames = ['full_name', 'email', 'phone', 'organization', 'position'];
  const isReservedFieldName = reservedFieldNames.includes(formData.field_name);
  const isDefaultField = field && reservedFieldNames.includes(field.field_name);

  useEffect(() => {
    if (field) {
      // Parse field_options if it's an array
      let optionsString = '';
      if (field.field_options) {
        if (Array.isArray(field.field_options)) {
          optionsString = field.field_options.join(', ');
        } else if (typeof field.field_options === 'string') {
          try {
            const parsed = JSON.parse(field.field_options);
            optionsString = Array.isArray(parsed) ? parsed.join(', ') : field.field_options;
          } catch {
            optionsString = field.field_options;
          }
        }
      }

      setFormData({
        field_name: field.field_name || '',
        field_label: field.field_label || '',
        field_type: field.field_type || 'text',
        field_options: optionsString,
        placeholder: field.placeholder || '',
        is_required: field.is_required || false,
        is_active: field.is_active !== undefined ? field.is_active : true,
        show_on_badge: field.show_on_badge || false,
        is_global: field.is_global || false,
      });
    } else {
      setFormData({
        field_name: '',
        field_label: '',
        field_type: 'text',
        field_options: '',
        placeholder: '',
        is_required: false,
        is_active: true,
        show_on_badge: false,
        is_global: false,
      });
    }
    setErrors({});
  }, [field, isOpen]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-generate field_name from field_label if creating new field
    if (name === 'field_label' && !field) {
      const generatedName = value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_');
      setFormData(prev => ({ ...prev, field_name: generatedName }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.field_label.trim()) {
      newErrors.field_label = 'Field label is required';
    }

    // Validate options for select/radio/checkbox types
    if (['select', 'radio', 'checkbox'].includes(formData.field_type)) {
      if (!formData.field_options.trim()) {
        newErrors.field_options = 'Options are required for this field type';
      }
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      console.log('Validation errors:', newErrors);
      return;
    }

    // Parse options into array
    let fieldOptions = null;
    if (['select', 'radio', 'checkbox'].includes(formData.field_type) && formData.field_options) {
      fieldOptions = formData.field_options
        .split(',')
        .map(opt => opt.trim())
        .filter(opt => opt.length > 0);
    }

    // Only send fields that exist in the database model
    const submitData = {
      field_name: formData.field_name,
      field_label: formData.field_label,
      field_type: formData.field_type,
      field_options: fieldOptions,
      is_required: formData.is_required,
      is_active: formData.is_active,
      show_on_badge: formData.show_on_badge,
      is_global: formData.is_global,
    };
    
    // Only add placeholder if it has a value (optional field)
    if (formData.placeholder && formData.placeholder.trim()) {
      submitData.placeholder = formData.placeholder;
    }

    console.log('Submitting field data:', submitData);
    onSave(submitData);
  };

  const handleClose = () => {
    setFormData({
      field_name: '',
      field_label: '',
      field_type: 'text',
      field_options: '',
      placeholder: '',
      is_required: false,
      is_active: true,
      show_on_badge: false,
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const requiresOptions = ['select', 'radio', 'checkbox'].includes(formData.field_type);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-2">
              <Type className="h-6 w-6 text-primary-600" />
              <h3 className="text-xl font-semibold text-gray-900">
                {field ? 'Edit Field' : 'Add New Field'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800">Please fix the following errors:</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <ul className="list-disc list-inside space-y-1">
                        {Object.entries(errors).map(([field, message]) => {
                          // Handle different error formats
                          let displayMessage = '';
                          if (typeof message === 'string') {
                            displayMessage = message;
                          } else if (Array.isArray(message)) {
                            displayMessage = message.join(', ');
                          } else if (typeof message === 'object') {
                            displayMessage = message.message || JSON.stringify(message);
                          } else {
                            displayMessage = String(message);
                          }
                          return <li key={field}>{displayMessage}</li>;
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Default Field Warning */}
            {isDefaultField && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-purple-800">Default Field</h3>
                    <p className="mt-1 text-sm text-purple-700">
                      This is a default field that exists for all events. Some properties cannot be modified to maintain system consistency.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reserved Field Name Warning */}
            {!isDefaultField && isReservedFieldName && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-yellow-800">Reserved Field Name</h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      This field name is reserved for default fields. Please choose a different name.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-4">
              <Input
                label="Field Label"
                value={formData.field_label}
                onChange={(e) => handleChange('field_label', e.target.value)}
                error={errors.field_label}
                required
                placeholder="e.g., Full Name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Field Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.field_type}
                  onChange={(e) => handleChange('field_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isDefaultField}
                >
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Placeholder Text"
                value={formData.placeholder}
                onChange={(e) => handleChange('placeholder', e.target.value)}
                placeholder="e.g., Enter your name"
              />
            </div>

            {requiresOptions && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Options <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.field_options}
                  onChange={(e) => handleChange('field_options', e.target.value)}
                  placeholder="Enter options separated by commas (e.g., Option 1, Option 2, Option 3)"
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.field_options ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.field_options && (
                  <p className="text-red-500 text-sm mt-1">{errors.field_options}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Separate multiple options with commas
                </p>
              </div>
            )}

            <div className="space-y-3 border-t pt-4">
              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_required}
                  onChange={(e) => handleChange('is_required', e.target.checked)}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Required Field</div>
                  <div className="text-xs text-gray-500">Users must fill this field</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.show_on_badge}
                  onChange={(e) => handleChange('show_on_badge', e.target.checked)}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Show on Badge</div>
                  <div className="text-xs text-gray-500">Display this field on printed badges</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Active</div>
                  <div className="text-xs text-gray-500">Show this field in registration forms</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_global}
                  onChange={(e) => handleChange('is_global', e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  disabled={!!field}
                />
                <div>
                  <div className="text-sm font-medium text-blue-900">Global Field (All Events)</div>
                  <div className="text-xs text-blue-700">
                    {field ? 'Cannot change after creation' : 'Make this field available to all events'}
                  </div>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 gap-2"
                loading={loading}
              >
                <Save className="h-5 w-5" />
                {field ? 'Update Field' : 'Create Field'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FieldFormModal;
